import React, { ComponentProps, ReactNode, useRef } from 'react';
import { Nullable } from 'src/types/nullable.type';
import { FloatingDelayGroup } from '@floating-ui/react';
import { useSpringRef, useSpring, animated, easings } from '@react-spring/web';
import classNames from 'classnames';
import { useClickAway, useUpdateEffect } from 'react-use';
import { NeueTooltip } from '../components/tooltip';
import { CreateToolbarPanel } from './panel';

export type ToolbarSectionItemType = {
  type: string;
  label: string;
  Icon: (props: ComponentProps<'svg'>) => JSX.Element;
  content?: object;
  disabled?: boolean;
  tooltip?: string;
};

const FLOAT_ANIMATION_CONFIG = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const CreateToolbarMenu = ({
  showMenu,
  onOpen,
  onAddItem,
  onClose,
  disabled = false,
}: {
  showMenu: boolean;
  onOpen: () => void;
  onClose: () => void;
  onAddItem: (item: ToolbarSectionItemType) => void;
  disabled?: boolean
}) => {
  const isClickedAwayRef = useRef(false);
  const containerElementRef = useRef<Nullable<HTMLDivElement>>(null);
  const [draggingElement, setDraggingElement] = React.useState<Nullable<ReactNode>>(null);

  const animationApi = useSpringRef();
  const [animationStyle] = useSpring(
    () => ({
      ref: animationApi,
      from: { opacity: 0, y: 8 },
      to: { opacity: 1 },
    }),
    []
  );

  useClickAway(containerElementRef, () => {
    if (showMenu) {
      isClickedAwayRef.current = true;
      onClose();
    }
  });

  useUpdateEffect(() => {
    if (showMenu) {
      animationApi.start({
        config: FLOAT_ANIMATION_CONFIG,
        to: { y: 0, opacity: 1 },
      });
    } else {
      animationApi.start({
        to: { opacity: 0 },
        config: { duration: 200, easing: easings.easeOutCubic },
      });
      setTimeout(() => {
        isClickedAwayRef.current = false;
      }, 200);
    }
  }, [showMenu]);

  const onMenuClick = () => {
    if (showMenu) {
      onClose();
    } else {
      onOpen();
    }
  };

  return (
    <>
      <FloatingDelayGroup delay={{ open: 10 }}>
        <NeueTooltip className='mt-1' offset={16} placement='left' tooltipContent='More blocks' forceHide={showMenu}>
          <div
            className={classNames(
              'group pointer-events-auto cursor-pointer',
              {
                'pointer-events-none cursor-not-allowed opacity-40': disabled,
              }
            )}
            onClick={() => {
              if (!isClickedAwayRef.current && !disabled) {
                onMenuClick();
              }
            }}
          >
            <div className='p-1.5'>
              <div className='w-5 h-5 m-auto flex items-center justify-center rounded transition-colors bg-neue-journey-fg group-hover:bg-neue-journey-fg-90'>
                <svg
                  className={classNames('w-3 h-3 text-neue-journey-bg transition', {
                    'rotate-45': showMenu,
                  })}
                  viewBox='0 0 12 12'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M6.74993 1.62305C6.74993 1.20883 6.41414 0.873047 5.99993 0.873047C5.58572 0.873047 5.24993 1.20883 5.24993 1.62305V5.24944H1.62354C1.20932 5.24944 0.873535 5.58523 0.873535 5.99944C0.873535 6.41365 1.20932 6.74944 1.62354 6.74944H5.24993L5.24993 10.3761C5.24993 10.7903 5.58572 11.1261 5.99993 11.1261C6.41414 11.1261 6.74993 10.7903 6.74993 10.3761L6.74993 6.74944L10.3766 6.74944C10.7908 6.74944 11.1266 6.41365 11.1266 5.99944C11.1266 5.58523 10.7908 5.24944 10.3766 5.24944L6.74993 5.24944V1.62305Z'
                    fill='currentColor'
                  />
                </svg>
              </div>
            </div>
          </div>
        </NeueTooltip>
      </FloatingDelayGroup>
      <div
        className={classNames(
          'absolute -translate-x-full -left-2 top-1/2 -translate-y-1/2 z-neue-block-control-panel-selected',
          {
            'pointer-events-auto': showMenu,
            'pointer-events-none': !showMenu,
          }
        )}
      >
        <animated.div
          ref={(el) => {
            if (el) {
              containerElementRef.current = el;
            }
          }}
          style={animationStyle}
        >
          <CreateToolbarPanel
            showPanel={showMenu}
            onDrag={(node, exceedThreshold) => {
              setDraggingElement(node);
              if (exceedThreshold && showMenu) {
                onClose();
              }
            }}
            onAddItem={(item) => {
              setDraggingElement(null);
              onAddItem(item);
              if (showMenu) {
                onClose();
              }
            }}
          />
        </animated.div>
      </div>
      {draggingElement}
    </>
  );
};
